/**
 * Log source onboarding.
 *
 * The screen the platform's cost argument lives on. Onboarding is a negotiation, not an upload:
 * the operator sees what each field costs and what it buys, and accepts a policy. Five steps,
 * each of which can be reviewed and changed before anything is saved.
 *
 * Three things are deliberate and worth not undoing:
 *
 *  1. **The preview calls the real parser.** What the operator sees on step 4 is exactly what
 *     ingestion will do, because it is the same function. A preview that approximated the
 *     pipeline would build confidence in a configuration that behaves differently once accepted.
 *  2. **Every proposed disposition carries its reason.** "Drop this field" without "because it is
 *     97% distinct and no detection filters on it" is an instruction, not a recommendation, and
 *     an operator cannot sensibly disagree with an instruction.
 *  3. **The cost comparison is shown, not asserted.** The saving against keeping everything is
 *     computed from the same model the platform uses, so the claim is checkable.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  api,
  type FieldDisposition,
  type FieldRule,
  type LogSource,
  type PreviewResponse,
  type Recommendation,
} from '../api'
import {
  Card,
  Chip,
  Mono,
  Note,
  fmtBytes,
  fmtNumber,
  useToast,
} from '../components/ui'

const DISPOSITIONS: { value: FieldDisposition; label: string; hint: string }[] = [
  { value: 'key_field', label: 'Key field', hint: 'Stored and indexed as an entity — correlation and threat-intel pivot on it' },
  { value: 'keep', label: 'Keep', hint: 'Stored for the hot window' },
  { value: 'drop', label: 'Drop', hint: 'Discarded at parse time. Never stored, never billed' },
  { value: 'hash', label: 'Hash', hint: 'Stored as a one-way token — joinable, not readable' },
  { value: 'redact', label: 'Redact', hint: 'Replaced with a marker, and scrubbed from the message and raw line too' },
]

const OBSERVABLE_KINDS = [
  'ip', 'domain', 'url', 'file_hash', 'file_name', 'user', 'host', 'process', 'email',
  'asn', 'certificate', 'mutex', 'registry_key',
]

const EVENT_CLASSES = [
  'authentication', 'process', 'network', 'dns', 'http', 'file', 'email',
  'registry', 'cloud', 'identity', 'alert', 'audit', 'malware', 'other',
]

const SAMPLE_PRESETS: Record<string, string> = {
  'Firewall (key=value)': [
    'host=fw-edge-1 action=deny srcip=10.0.4.17 dstip=203.0.113.9 dstport=445 proto=tcp bytes_out=1204 sessionid=s-8fa21c04',
    'host=fw-edge-1 action=allow srcip=10.0.4.22 dstip=198.51.100.7 dstport=443 proto=tcp bytes_out=884213 sessionid=s-8fa21c05',
    'host=fw-edge-1 action=deny srcip=10.0.4.31 dstip=203.0.113.44 dstport=22 proto=tcp bytes_out=0 sessionid=s-8fa21c06',
  ].join('\n'),
  'Endpoint (JSON)': [
    '{"@timestamp":"2026-09-01T10:14:02.331Z","host":"WIN-WS14","user":"a.hale","process":{"name":"powershell.exe","path":"C:\\\\Windows\\\\System32\\\\powershell.exe","command_line":"powershell -nop -w hidden -enc SQBFAFgA"},"message":"process started"}',
    '{"@timestamp":"2026-09-01T10:14:07.902Z","host":"WIN-WS14","user":"a.hale","process":{"name":"cmd.exe","path":"C:\\\\Windows\\\\System32\\\\cmd.exe","command_line":"cmd /c whoami"},"message":"process started"}',
  ].join('\n'),
  'Appliance (CEF)': [
    'CEF:0|Palo Alto|PAN-OS|10.2|threat|Spyware detected|8|src=10.0.5.20 dst=203.0.113.77 spt=51422 dpt=443 act=alert cs1=Suspicious',
    'CEF:0|Palo Alto|PAN-OS|10.2|traffic|Session end|3|src=10.0.5.21 dst=198.51.100.3 spt=51002 dpt=80 act=allow',
  ].join('\n'),
  'Syslog (RFC 3164)': [
    '<38>Sep  1 10:22:41 auth-gw sshd[4821]: Failed password for invalid user admin from 203.0.113.44 port 51233 ssh2',
    '<38>Sep  1 10:22:44 auth-gw sshd[4823]: Failed password for invalid user root from 203.0.113.44 port 51241 ssh2',
  ].join('\n'),
}

export default function Onboarding() {
  const navigate = useNavigate()
  const toast = useToast()

  const [step, setStep] = useState(0)
  const [id, setId] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [vendor, setVendor] = useState('')
  const [sampleText, setSampleText] = useState('')
  const [csvColumns, setCsvColumns] = useState('')
  const [rec, setRec] = useState<Recommendation | null>(null)
  const [source, setSource] = useState<LogSource | null>(null)
  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [busy, setBusy] = useState(false)

  const sample = useMemo(() => sampleText.split('\n').filter((l) => l.trim()), [sampleText])

  function analyze() {
    setBusy(true)
    api
      .post<Recommendation>('/onboarding/recommend', {
        id: id.trim(),
        display_name: displayName.trim() || id.trim(),
        sample,
        csv_columns: csvColumns.split(',').map((s) => s.trim()).filter(Boolean),
        vendor_hint: vendor.trim(),
      })
      .then((r) => {
        setRec(r)
        setSource(r.source)
        setStep(1)
      })
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setBusy(false))
  }

  function runPreview(next: LogSource) {
    setBusy(true)
    api
      .post<PreviewResponse>('/onboarding/preview', { source: next, sample })
      .then((p) => {
        setPreview(p)
        setStep(4)
      })
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setBusy(false))
  }

  function save() {
    if (!source) return
    setBusy(true)
    api
      .put<{ warnings: string[] }>('/sources', source)
      .then((r) => {
        toast('ok', r.warnings.length ? `Saved with ${r.warnings.length} warning(s)` : 'Log source saved')
        navigate('/sources')
      })
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setBusy(false))
  }

  function updateRule(field: string, patch: Partial<FieldRule>) {
    if (!source) return
    setSource({
      ...source,
      field_rules: source.field_rules.map((r) => (r.source_field === field ? { ...r, ...patch } : r)),
    })
  }

  const steps = ['Sample', 'Format', 'Fields', 'Retention', 'Preview']

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Onboard a log source</h1>
          <div className="sub">
            Decide once what this source is worth keeping. The platform profiles a sample, proposes
            a policy with a reason for every field, and shows what the choice costs — before
            anything is stored.
          </div>
        </div>
        <div className="actions">
          <button onClick={() => navigate('/sources')}>Cancel</button>
        </div>
      </div>

      <div className="steps">
        {steps.map((label, i) => (
          <div key={label} className={`step${i === step ? ' active' : i < step ? ' done' : ''}`}>
            <span className="n">{i < step ? '✓' : i + 1}</span>
            {label}
          </div>
        ))}
      </div>

      {step === 0 && (
        <Card title="Give the platform a sample">
          <div className="grid cols-3" style={{ marginBottom: 12 }}>
            <div className="field">
              <label htmlFor="src-id">Source id</label>
              <input
                id="src-id"
                placeholder="fw-edge-1"
                value={id}
                onChange={(e) => setId(e.target.value.replace(/[^a-zA-Z0-9._-]/g, ''))}
              />
              <div className="hint">Used in queries and rules. Lower case, no spaces.</div>
            </div>
            <div className="field">
              <label htmlFor="src-name">Display name</label>
              <input id="src-name" placeholder="Edge firewall" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="src-vendor">Vendor / product (optional)</label>
              <input id="src-vendor" placeholder="Palo Alto PAN-OS" value={vendor} onChange={(e) => setVendor(e.target.value)} />
              <div className="hint">Yours, not guessed — product identification from a log sample is unreliable.</div>
            </div>
          </div>

          <div className="field">
            <label htmlFor="sample">Sample lines</label>
            <div className="row wrap" style={{ marginBottom: 6 }}>
              <span className="small muted">Start from an example:</span>
              {Object.keys(SAMPLE_PRESETS).map((k) => (
                <button key={k} className="sm ghost" onClick={() => setSampleText(SAMPLE_PRESETS[k] ?? '')}>
                  {k}
                </button>
              ))}
              <label className="btn sm" style={{ margin: 0 }}>
                Upload a file
                <input
                  type="file"
                  accept=".log,.txt,.json,.csv,.ndjson"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (!f) return
                    // Read a bounded slice: a multi-gigabyte log must not be loaded into the tab
                    // to profile a few hundred lines from it.
                    f.slice(0, 2_000_000)
                      .text()
                      .then((t) => setSampleText(t.split('\n').slice(0, 2000).join('\n')))
                      .catch(() => toast('error', 'Could not read that file'))
                  }}
                />
              </label>
            </div>
            <textarea
              id="sample"
              rows={14}
              placeholder="Paste raw log lines here — 200 or more spanning a busy period gives much better recommendations than a handful."
              value={sampleText}
              onChange={(e) => setSampleText(e.target.value)}
            />
            <div className="hint">
              {sample.length} line(s). The sample never leaves this deployment.
            </div>
          </div>

          <div className="field">
            <label htmlFor="csv">CSV column header (only if this source is CSV)</label>
            <input id="csv" placeholder="timestamp, user, action, result" value={csvColumns} onChange={(e) => setCsvColumns(e.target.value)} />
          </div>

          <button className="primary" disabled={busy || !id.trim() || sample.length === 0} onClick={analyze}>
            {busy ? 'Profiling…' : 'Profile the sample'}
          </button>
        </Card>
      )}

      {step === 1 && rec && source && (
        <div className="stack">
          <Card title="Detected format" subtitle="Voted across the whole sample, not read off the first line">
            <div className="row wrap" style={{ marginBottom: 12 }}>
              <Chip tone={rec.format_confidence > 0.7 ? 'ok' : 'warn'}>
                {source.format} · {Math.round(rec.format_confidence * 100)}% confidence
              </Chip>
              {rec.format_alternatives.slice(1, 4).map(([f, s]) => (
                <Chip key={f} tone="outline">
                  {f} {Math.round(s * 100)}%
                </Chip>
              ))}
            </div>

            {rec.notes.map((n, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <Note tone={n.includes('weak') || n.includes('Only') ? 'warn' : 'info'}>{n}</Note>
              </div>
            ))}

            <div className="grid cols-3">
              <div className="field">
                <label>Override the format</label>
                <select value={source.format} onChange={(e) => setSource({ ...source, format: e.target.value })}>
                  {['syslog_3164', 'syslog_5424', 'json', 'cef', 'leef', 'key_value', 'csv', 'unstructured'].map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Default event class</label>
                <select value={source.default_class} onChange={(e) => setSource({ ...source, default_class: e.target.value })}>
                  {EVENT_CLASSES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <div className="hint">Rules filter on this before touching any field.</div>
              </div>
              <div className="field">
                <label>Severity field</label>
                <select
                  value={source.severity_field ?? ''}
                  onChange={(e) => setSource({ ...source, severity_field: e.target.value || null })}
                >
                  <option value="">— use the default —</option>
                  {rec.profiles.map((p) => (
                    <option key={p.name} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <div className="hint">Values naming a severity directly are understood without a map.</div>
              </div>
            </div>
          </Card>
          <div className="row">
            <button onClick={() => setStep(0)}>Back</button>
            <button className="primary" onClick={() => setStep(2)}>
              Review fields ({rec.profiles.length})
            </button>
          </div>
        </div>
      )}

      {step === 2 && rec && source && (
        <div className="stack">
          <Card
            title="What to keep"
            subtitle="Every proposal carries its reason. Disagree freely — this is the decision, not a formality."
            tight
          >
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Profile</th>
                    <th>Handling</th>
                    <th>Store as</th>
                    <th>Entity</th>
                    <th>Why</th>
                  </tr>
                </thead>
                <tbody>
                  {rec.profiles.map((p) => {
                    const rule = source.field_rules.find((r) => r.source_field === p.name)
                    if (!rule) return null
                    const advice = rec.field_advice.find((a) => a.field === p.name)
                    return (
                      <tr key={p.name}>
                        <td>
                          <Mono>{p.name}</Mono>
                          <div className="faint small">{p.value_type}</div>
                        </td>
                        <td className="small muted nowrap">
                          <div>{p.fill_pct}% present</div>
                          <div className={p.distinct_pct >= 95 ? 'chip danger' : ''}>
                            {p.distinct_pct}% distinct
                          </div>
                          <div className="faint truncate" title={p.examples.join(' · ')} style={{ maxWidth: '22ch' }}>
                            {p.examples[0] ?? ''}
                          </div>
                        </td>
                        <td>
                          <select
                            value={rule.disposition}
                            aria-label={`Handling for ${p.name}`}
                            onChange={(e) =>
                              updateRule(p.name, {
                                disposition: e.target.value as FieldDisposition,
                                // A dropped or redacted field cannot be an entity: the value the
                                // pivot needs is gone. Clearing it here prevents saving a config
                                // the validator would (rightly) complain about.
                                ...(e.target.value === 'drop' || e.target.value === 'redact' || e.target.value === 'hash'
                                  ? { observable: null }
                                  : {}),
                              })
                            }
                            title={DISPOSITIONS.find((d) => d.value === rule.disposition)?.hint}
                          >
                            {DISPOSITIONS.map((d) => (
                              <option key={d.value} value={d.value} title={d.hint}>
                                {d.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            value={rule.rename_to ?? ''}
                            placeholder={p.name}
                            aria-label={`Rename ${p.name}`}
                            onChange={(e) => updateRule(p.name, { rename_to: e.target.value || null })}
                            style={{ minWidth: 120 }}
                          />
                          {advice?.suggested_name && advice.suggested_name !== rule.rename_to && (
                            <button
                              className="sm ghost"
                              onClick={() => updateRule(p.name, { rename_to: advice.suggested_name ?? null })}
                            >
                              use {advice.suggested_name}
                            </button>
                          )}
                        </td>
                        <td>
                          <select
                            value={rule.observable ?? ''}
                            aria-label={`Entity kind for ${p.name}`}
                            disabled={rule.disposition === 'drop' || rule.disposition === 'redact' || rule.disposition === 'hash'}
                            onChange={(e) =>
                              updateRule(p.name, {
                                observable: e.target.value || null,
                                disposition: e.target.value ? 'key_field' : rule.disposition,
                              })
                            }
                          >
                            <option value="">—</option>
                            {OBSERVABLE_KINDS.map((k) => (
                              <option key={k} value={k}>
                                {k}
                              </option>
                            ))}
                          </select>
                          {rule.observable && (
                            <input
                              value={rule.observable_role ?? ''}
                              placeholder="role (src/dst)"
                              aria-label={`Role for ${p.name}`}
                              onChange={(e) => updateRule(p.name, { observable_role: e.target.value })}
                              style={{ marginTop: 3, minWidth: 100 }}
                            />
                          )}
                        </td>
                        <td className="small muted" style={{ maxWidth: '38ch' }}>
                          {rule.rationale}
                          {advice && (
                            <div style={{ marginTop: 4 }}>
                              <Chip tone="outline">AI</Chip> {advice.meaning}
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {!rec.ai_available && (
            <Note>
              No AI advisor is configured, so the reasons above come from the deterministic
              profiler. That is the whole recommendation — a model would add product-specific
              commentary, not different decisions.
            </Note>
          )}

          <div className="row">
            <button onClick={() => setStep(1)}>Back</button>
            <button className="primary" onClick={() => setStep(3)}>
              Retention and cost
            </button>
          </div>
        </div>
      )}

      {step === 3 && rec && source && (
        <div className="stack">
          <div className="grid cols-2">
            <Card title="Retention" subtitle="How long each tier lives, and what gets kept at all">
              <div className="grid cols-2">
                <div className="field">
                  <label>Hot window (days)</label>
                  <input
                    type="number"
                    min={1}
                    value={source.retention.hot_days}
                    onChange={(e) =>
                      setSource({ ...source, retention: { ...source.retention, hot_days: Number(e.target.value) } })
                    }
                  />
                  <div className="hint">Full-fidelity, fully queryable.</div>
                </div>
                <div className="field">
                  <label>Rollup window (days)</label>
                  <input
                    type="number"
                    min={0}
                    value={source.retention.warm_days}
                    onChange={(e) =>
                      setSource({ ...source, retention: { ...source.retention, warm_days: Number(e.target.value) } })
                    }
                  />
                  <div className="hint">Pre-aggregated. Orders of magnitude smaller.</div>
                </div>
              </div>

              <div className="field">
                <label className="row" style={{ gap: 7 }}>
                  <input
                    type="checkbox"
                    checked={source.retention.keep_raw}
                    onChange={(e) =>
                      setSource({ ...source, retention: { ...source.retention, keep_raw: e.target.checked } })
                    }
                  />
                  Keep the original raw line
                </label>
                <div className="hint">
                  The single biggest cost lever, and the one a per-gigabyte SIEM pulls the wrong
                  way. Worth it for prose-heavy sources where the parsed fields carry little.
                </div>
              </div>

              {source.retention.keep_raw && (
                <div className="field">
                  <label>Raw retention (days)</label>
                  <input
                    type="number"
                    min={1}
                    value={source.retention.raw_days}
                    onChange={(e) =>
                      setSource({ ...source, retention: { ...source.retention, raw_days: Number(e.target.value) } })
                    }
                  />
                  <div className="hint">Usually far shorter than the hot window — raw is for recent forensics.</div>
                </div>
              )}

              <div className="grid cols-2">
                <div className="field">
                  <label>Keep 1 event in…</label>
                  <input
                    type="number"
                    min={1}
                    value={source.retention.sample_one_in}
                    onChange={(e) =>
                      setSource({ ...source, retention: { ...source.retention, sample_one_in: Math.max(1, Number(e.target.value)) } })
                    }
                  />
                  <div className="hint">1 keeps everything. For sources whose value is statistical.</div>
                </div>
                <div className="field">
                  <label>Never sample at or above</label>
                  <select
                    value={source.retention.sampling_floor}
                    onChange={(e) =>
                      setSource({ ...source, retention: { ...source.retention, sampling_floor: e.target.value as never } })
                    }
                  >
                    {['info', 'low', 'medium', 'high', 'critical'].map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <div className="hint">Sampling a Critical away to save disk is how a breach gets missed.</div>
                </div>
              </div>

              <div className="field">
                <label className="row" style={{ gap: 7 }}>
                  <input
                    type="checkbox"
                    checked={source.retention.keep_unmapped}
                    onChange={(e) =>
                      setSource({ ...source, retention: { ...source.retention, keep_unmapped: e.target.checked } })
                    }
                  />
                  Store fields no rule mentions
                </label>
                <div className="hint">
                  Useful while discovering a source. As a habit, it is how a vendor's next firmware
                  update silently starts costing money.
                </div>
              </div>
            </Card>

            <Card title="What this costs" subtitle="Against keeping everything, at one million events per day">
              <div className="grid cols-2" style={{ marginBottom: 14 }}>
                <div>
                  <div className="label small faint">Your policy</div>
                  <div style={{ fontSize: 24, fontWeight: 620 }} className="tnum">
                    {fmtBytes(rec.estimated_bytes_per_event * 1_000_000)}
                  </div>
                  <div className="small muted">per day</div>
                </div>
                <div>
                  <div className="label small faint">Keeping everything</div>
                  <div style={{ fontSize: 24, fontWeight: 620, color: 'var(--text-muted)' }} className="tnum">
                    {fmtBytes(rec.estimated_bytes_if_keep_all * 1_000_000)}
                  </div>
                  <div className="small muted">per day</div>
                </div>
              </div>
              <Note tone={rec.savings_pct > 30 ? 'ok' : 'info'}>
                <strong>{rec.savings_pct}% smaller</strong> than storing every field and every raw
                line — about {fmtBytes((rec.estimated_bytes_if_keep_all - rec.estimated_bytes_per_event) * 365_000_000)}{' '}
                a year at this volume. The estimate moves as you change the policy above.
              </Note>
              <hr />
              <dl className="kv">
                <dt>Fields kept</dt>
                <dd className="tnum">
                  {source.field_rules.filter((r) => r.disposition !== 'drop').length} of {source.field_rules.length}
                </dd>
                <dt>Entity fields</dt>
                <dd className="tnum">{source.field_rules.filter((r) => r.observable).length}</dd>
                <dt>Redacted</dt>
                <dd className="tnum">{source.field_rules.filter((r) => r.disposition === 'redact').length}</dd>
              </dl>
              <div className="field" style={{ marginTop: 12 }}>
                <label className="row" style={{ gap: 7 }}>
                  <input
                    type="checkbox"
                    checked={source.scan_message_for_observables}
                    onChange={(e) => setSource({ ...source, scan_message_for_observables: e.target.checked })}
                  />
                  Also scan the message text for entities
                </label>
                <div className="hint">
                  Worth it for free-text sources. Wasteful when every entity is already a named field.
                </div>
              </div>
            </Card>
          </div>

          <div className="row">
            <button onClick={() => setStep(2)}>Back</button>
            <button className="primary" disabled={busy} onClick={() => runPreview(source)}>
              {busy ? 'Parsing…' : 'Preview what gets stored'}
            </button>
          </div>
        </div>
      )}

      {step === 4 && source && preview && (
        <div className="stack">
          {preview.config_warnings.length > 0 && (
            <Note tone="warn">
              <strong>Before you save:</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {preview.config_warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </Note>
          )}

          {preview.unmapped_fields.length > 0 && (
            <Note>
              This source also emits {preview.unmapped_fields.length} field(s) no rule mentions:{' '}
              <Mono>{preview.unmapped_fields.join(', ')}</Mono>.{' '}
              {source.retention.keep_unmapped
                ? 'They will be stored because "store unmapped fields" is on.'
                : 'They will be discarded.'}
            </Note>
          )}

          <Card
            title={`Parsed events (${preview.events.length} of ${sample.length} sample lines)`}
            subtitle="Produced by the same parser ingestion uses — this is exactly what will be stored"
            tight
          >
            {preview.dropped_by_sampling > 0 && (
              <div style={{ padding: 12 }}>
                <Note>{preview.dropped_by_sampling} line(s) were sampled away by the retention policy.</Note>
              </div>
            )}
            <div className="table-wrap" style={{ maxHeight: 460, overflowY: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Class</th>
                    <th>Severity</th>
                    <th>Message</th>
                    <th>Stored fields</th>
                    <th>Entities</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.events.map((e) => (
                    <tr key={e.id}>
                      <td><Chip tone="outline">{e.class}</Chip></td>
                      <td><Chip tone={e.severity}>{e.severity}</Chip></td>
                      <td className="small truncate" title={e.message} style={{ maxWidth: '40ch' }}>
                        {e.message}
                      </td>
                      <td className="small mono" style={{ maxWidth: '34ch' }}>
                        {Object.entries(e.fields).map(([k, v]) => (
                          <div key={k} className="truncate">
                            <span className="faint">{k}=</span>
                            {String(v)}
                          </div>
                        ))}
                      </td>
                      <td className="small">
                        {e.observables.map((o, i) => (
                          <div key={i}>
                            <Chip tone="outline">
                              {o.kind}
                              {o.role ? `:${o.role}` : ''}
                            </Chip>{' '}
                            <span className="mono">{o.value}</span>
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {preview.events.length === 0 && (
            <Note tone="danger">
              Nothing parsed. The format is probably wrong for this sample — go back to step 2 and
              try one of the alternatives.
            </Note>
          )}

          <div className="row">
            <button onClick={() => setStep(3)}>Back</button>
            <button className="primary" disabled={busy || preview.events.length === 0} onClick={save}>
              {busy ? 'Saving…' : `Save “${source.display_name}”`}
            </button>
            <span className="small muted">
              {fmtNumber(source.field_rules.filter((r) => r.disposition !== 'drop').length)} fields kept ·{' '}
              {source.retention.keep_raw ? 'raw retained' : 'raw discarded'} ·{' '}
              {source.retention.hot_days}d hot
            </span>
          </div>
        </div>
      )}
    </>
  )
}
